import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileSpreadsheet, FileText, RefreshCw, FileType } from 'lucide-react';
import axios from 'axios';
import { sw } from '../../lib/sitewalkApi';

const REPORTS = [
  { type: 'site-walk-register', label: 'Site Walk Register' },
  { type: 'coverage', label: '360 Capture Coverage' },
  { type: 'location-history', label: 'Location Capture History' },
];

/** Registers + factual report exports (CSV/XLSX). Same backend authorization as the screens. */
export default function RegisterReports({ projectId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setRows((await sw.walks(projectId, { pageSize: 500 })).items || []); }
    catch (e) { toast.error('Failed to load register'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const download = async (type, fmt) => {
    try {
      const url = sw.reportUrl(projectId, type, fmt);
      const resp = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([resp.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = `${type}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${type}.${fmt} downloaded`);
    } catch (e) { toast.error('Export not permitted'); }
  };

  return (
    <div data-testid="register-reports">
      <div className="grid sm:grid-cols-3 gap-2 mb-4">
        {REPORTS.map((r) => (
          <div key={r.type} className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`report-card-${r.type}`}>
            <div className="text-sm font-semibold text-slate-700 mb-2">{r.label}</div>
            <div className="flex gap-2">
              <button onClick={() => download(r.type, 'csv')} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border text-slate-600" data-testid={`report-${r.type}-csv`}><FileText className="w-3.5 h-3.5" /> CSV</button>
              <button onClick={() => download(r.type, 'xlsx')} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border text-slate-600" data-testid={`report-${r.type}-xlsx`}><FileSpreadsheet className="w-3.5 h-3.5" /> XLSX</button>
              <button onClick={() => download(r.type, 'pdf')} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border text-slate-600" data-testid={`report-${r.type}-pdf`}><FileType className="w-3.5 h-3.5" /> PDF</button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mb-3">Branded PDF, CSV and XLSX all use the certified 7D-A exports (contractor-scoped, factual — capture coverage is not construction progress).</p>

      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-700">Site Walk Register (preview)</h4>
        <button onClick={load} className="text-xs flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-slate-600" data-testid="register-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="register-table">
        {loading ? <p className="text-sm text-slate-400 p-3">Loading…</p> : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr>{['Walk', 'Name', 'Status', 'Method', 'Points', 'Captured', 'Created'].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.id} className="border-t" data-testid={`register-row-${w.walkNumber}`}>
                  <td className="px-3 py-2 font-medium text-slate-700">{w.walkNumber}</td>
                  <td className="px-3 py-2 text-slate-600">{w.name}</td>
                  <td className="px-3 py-2">{w.status}</td>
                  <td className="px-3 py-2 text-slate-500">{w.captureMethod}</td>
                  <td className="px-3 py-2">{w.capturePointCount || 0}</td>
                  <td className="px-3 py-2 text-slate-500">{(w.completedAt || w.captureDate || '').slice(0, 10)}</td>
                  <td className="px-3 py-2 text-slate-500">{(w.createdAt || '').slice(0, 10)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400">No site walks visible to you.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
